// @flow
import { useEffect, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { Checkbox, Text, View } from 'reshaped';

import { Link } from '@gandi/reshaped';

import { useTranslator } from '@gandi/translate';

import { ResellerCheckbox } from './ResellerCheckbox';

import styles from './Checkbox.module.css';

export function ResellerBlock({
  createMode,
}: {
  createMode: boolean,
}): React.MixedElement | null {
  const { __ } = useTranslator();
  const [open, setOpen] = useState<boolean>(false);

  const ref = useRef(null);

  const form = useFormContext();
  const type = form.watch('type');
  const reseller = form.watch('reseller');

  function onChange({ checked: currentChecked }: { checked: boolean }) {
    setOpen(currentChecked);
  }

  function scrollIntoView(block?: 'start' | 'end' = 'start') {
    const target = ref.current;
    if (!target) return;

    // Find the first truly scrollable ancestor.
    const container =
      target.closest('[class*="Modal"]') ??
      (function findScrollParent(el: HTMLElement) {
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
          const { overflowY } = getComputedStyle(parent);
          if (
            /(auto|scroll)/.test(overflowY) &&
            parent.scrollHeight > parent.clientHeight
          ) {
            return parent;
          }
          parent = parent.parentElement;
        }
        return null;
      })(target);

    // case that say we are not in a modal
    if (!container) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const offset =
      block === 'end'
        ? targetRect.bottom - containerRect.bottom
        : targetRect.top - containerRect.top;

    container.scrollBy({ top: offset, behavior: 'smooth' });
  }

  const { setValue, clearErrors } = form;

  useEffect(() => {
    if (!open && reseller !== false) {
      setValue('reseller', false);
      clearErrors('reseller');
    }
  }, [open, reseller, setValue, clearErrors]);

  if (type !== 'company') {
    return null;
  }

  return (
    <View>
      <View paddingBlock={4}>
        <Text variant="body-2" weight="bold">
          {__('Settings')}
        </Text>
      </View>

      <View
        padding={3}
        border
        borderColor="neutral"
        borderRadius="large"
        gap={5}
        className={styles.container}
      >
        {/* $FlowFixMe[prop-missing] */}
        <Checkbox name="showReseller" onChange={onChange} checked={open}>
          <Text variant="body-2">{__('I will resell the product')}</Text>
        </Checkbox>
        {open && (
          <View
            paddingStart={7}
            gap={6}
            attributes={{
              ref,
              style: {
                scrollMarginBottom: '108px', // button block height + parent padding
              },
            }}
          >
            <div>
              <Text as="p" variant="body-2" color="neutral" weight="bold">
                {__('About reseller organizations')}
              </Text>

              <View
                as="ul"
                paddingStart={4}
                paddingBlock={2}
                attributes={{ style: { listStyleType: 'disc' } }}
              >
                <Text as="li" variant="body-2" color="neutral">
                  {__(
                    'A reseller organization manages Gandi products for its customers.',
                  )}
                </Text>
                <Text as="li" variant="body-2" color="neutral">
                  {__(
                    'Only the reseller can access the domains, customers cannot.',
                  )}
                </Text>
                <Text as="li" variant="body-2" color="neutral">
                  {__('This setting cannot be changed later.')}
                </Text>
                <Link
                  href={__(
                    'https://docs.gandi.net/en/reseller/reseller_benefits/reseller_vs_organization.html',
                  )}
                  attributes={{
                    target: '_blank',
                  }}
                >
                  <Text variant="body-2"> {__('Learn more')}</Text>
                </Link>
              </View>
            </div>

            <ResellerCheckbox
              scrollIntoView={scrollIntoView}
              createMode={createMode}
            />
          </View>
        )}
      </View>
    </View>
  );
}
